import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthStorage, AuthUser } from "../components/services/storage/AuthStorage";
import { UserDeviceService } from "../components/services/UserDeviceService";
import { SessionService } from "../components/shared/services/SessionService";

type AuthContextValue = {
  user: AuthUser | null;
  isHydrating: boolean;
  setUser: (u: AuthUser | null) => Promise<void>;
  logout: (options?: { deleteDevice?: boolean }) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [isHydrating, setHydrating] = useState(true);

  const setUser = async (u: AuthUser | null) => {
    if (u) {
      const uuid = u.uuid?.trim() || await UserDeviceService.getCurrentUuid();
      const nextUser = { ...u, uuid };

      await UserDeviceService.setCurrentUuid(uuid);
      await AuthStorage.setUser(nextUser);
      if (u.token) await SessionService.setAccessToken(u.token);
      else await SessionService.clearSession();
      setUserState(nextUser);
    } else {
      await AuthStorage.clear();
      await SessionService.clearSession();
      setUserState(null);
    }
  };

  const logout = useCallback(async (options?: { deleteDevice?: boolean }) => {
    const shouldDeleteDevice = options?.deleteDevice ?? true;
    
    if (shouldDeleteDevice) {
      try {
        await UserDeviceService.deleteCurrentDevice();
      } catch (error) {
        console.log("Failed to delete user device on logout:", error);
      }
    }

    await setUser(null);
  }, []);

  useEffect(() => {
    (async () => {
      const saved = await AuthStorage.getUser();
      if (saved?.token) await SessionService.setAccessToken(saved.token);
      else await SessionService.clearSession();
      setUserState(saved);
      setHydrating(false);
    })();
  }, []);

  useEffect(() => {
    SessionService.registerSessionExpiredHandler(logout);

    return () => {
      SessionService.registerSessionExpiredHandler(null);
    };
  }, [logout]);

  const value = useMemo(
    () => ({ user, isHydrating, setUser, logout }),
    [user, isHydrating, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
